import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "list", "tags", "empty", "addButton"]
  static values = { url: String, selected: Array, missingEmails: Array, disabled: Boolean }

  connect() {
    this.debounceTimer = null
    this.page = 1
    this.query = ""
    this.hasMore = true
    this.loading = false
    this.selectedIndex = -1
    this.selected = this.selectedValue.map(t => ({ id: t.id ?? t.user_id, name: t.name, email: t.email }))
    this.boundHide = this.hide.bind(this)
    document.addEventListener("click", this.boundHide)
    this.form = this.element.closest("form")
    if (this.form) {
      this.boundFlushPending = this.flushPending.bind(this)
      this.form.addEventListener("submit", this.boundFlushPending)
    }
    this.renderTags()
    this.updateAddButtonState()
  }

  disconnect() {
    document.removeEventListener("click", this.boundHide)
    if (this.form && this.boundFlushPending) {
      this.form.removeEventListener("submit", this.boundFlushPending)
    }
    clearTimeout(this.debounceTimer)
  }

  open() {
    if (!this.listTarget.classList.contains("hidden")) return
    this.resetPaging()
    this.listTarget.innerHTML = ""
    this.fetchAndAppend()
  }

  input() {
    this.updateAddButtonState()
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.resetPaging()
      this.query = this.inputTarget.value.trim()
      this.listTarget.innerHTML = ""
      this.fetchAndAppend()
    }, 150)
  }

  navigate(event) {
    const options = this.selectableOptions()

    if (event.key === "Enter") {
      event.preventDefault()
      if (this.selectedIndex >= 0 && options[this.selectedIndex]) {
        const opt = options[this.selectedIndex]
        this.addTechnician({ id: opt.dataset.userId, name: opt.dataset.name, email: opt.dataset.email })
      } else {
        this.addByEmail()
      }
      return
    }

    if (options.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.selectedIndex = Math.min(this.selectedIndex + 1, options.length - 1)
      this.highlight(options)
      if (this.selectedIndex >= options.length - 3) this.fetchAndAppend()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
      this.highlight(options)
    } else if (event.key === "Escape") {
      this.closeList()
    }
  }

  scroll() {
    const list = this.listTarget
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 80) this.fetchAndAppend()
  }

  async fetchAndAppend() {
    if (this.loading || !this.hasMore) return
    this.loading = true

    const url = new URL(this.urlValue, window.location.origin)

    if (this.query) {
      url.searchParams.set("q[first_name_or_last_name_or_email_cont]", this.query)
    }
    url.searchParams.set("page", this.page)

    const response = await fetch(url, { headers: { Accept: "application/json" } })
    const { results, has_more } = await response.json()

    this.hasMore = has_more
    this.loading = false

    const alreadySelectedIds = new Set(this.selected.map(t => t.id).filter(Boolean))
    const filtered = results.filter(r => !alreadySelectedIds.has(r.id))

    if (filtered.length === 0 && !has_more) {
      if (this.listTarget.children.length === 0) this.closeList()
      return
    }

    filtered.forEach(item => this.appendOption(item))
    this.page++
    this.listTarget.classList.remove("hidden")
    this.positionList()
  }

  appendOption(item) {
    const li = document.createElement("li")
    li.dataset.userId = item.id
    li.dataset.name = item.name
    li.dataset.email = item.email
    li.className = "px-4 py-2 cursor-pointer hover:bg-gray-50 text-sm text-[#1c1b1f] flex flex-col"
    li.innerHTML = `<span class="font-medium">${this.escape(item.name)}</span><span class="text-xs text-[#49454f]">${this.escape(item.email)}</span>`
    li.addEventListener("mousedown", (e) => {
      e.preventDefault()
      this.addTechnician({ id: item.id, name: item.name, email: item.email })
    })
    this.listTarget.appendChild(li)
  }

  addTechnician(technician) {
    const alreadyAdded = this.selected.some(t =>
      (technician.id && t.id === technician.id) || t.email === technician.email
    )
    if (!alreadyAdded) {
      this.selected.push(technician)
      this.renderTags()
    }
    this.inputTarget.value = ""
    this.updateAddButtonState()
    this.closeList()
  }

  addByEmail() {
    const email = this.inputTarget.value.trim()
    if (!email) return
    this.addTechnician({ email })
  }

  add(event) {
    event.preventDefault()
    if (!this.inputTarget.value.trim()) return
    this.addByEmail()
    this.inputTarget.focus()
  }

  updateAddButtonState() {
    if (!this.hasAddButtonTarget) return
    this.addButtonTarget.disabled = this.inputTarget.value.trim() === ""
  }

  flushPending() {
    const value = this.inputTarget.value.trim()
    if (value.includes("@")) this.addByEmail()
  }

  remove(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    const removed = this.selected[index]
    this.selected.splice(index, 1)
    this.renderTags()

    const missing = this.missingEmailsValue
    if (missing.includes(removed?.email)) {
      const stillMissing = this.selected.some(t => missing.includes(t.email))
      if (!stillMissing) {
        this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }
  }

  renderTags() {
    this.tagsTarget.innerHTML = ""

    // Sentinel: always present so the controller knows technicians were managed
    const sentinel = document.createElement("input")
    sentinel.type = "hidden"
    sentinel.name = "inspection[technicians_present]"
    sentinel.value = "1"
    this.tagsTarget.appendChild(sentinel)

    this.selected.forEach((t, i) => {
      const removeButton = `<button type="button" data-action="technician-selector#remove" data-index="${i}"
                  class="text-secondary-500 hover:text-secondary-800 leading-none">&times;</button>`

      const tag = document.createElement("div")
      tag.className = "flex items-center gap-1.5 bg-secondary-50 border border-secondary-200 text-secondary-800 rounded-full px-3 py-1 text-sm"
      tag.innerHTML = `
        <span>${this.escape(t.name || t.email)}</span>
        ${removeButton}
        ${t.id ? `<input type="hidden" name="inspection[technicians][][user_id]" value="${this.escape(t.id)}">` : ""}
        <input type="hidden" name="inspection[technicians][][email]" value="${this.escape(t.email || "")}">
      `
      this.tagsTarget.appendChild(tag)
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", this.selected.length > 0)
    }
  }

  positionList() {
    const list = this.listTarget
    const rect = this.element.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom

    if (spaceBelow < list.offsetHeight) {
      list.classList.remove("top-full", "mt-1")
      list.classList.add("bottom-full", "mb-1")
    } else {
      list.classList.remove("bottom-full", "mb-1")
      list.classList.add("top-full", "mt-1")
    }
  }

  closeList() {
    this.listTarget.classList.add("hidden")
    this.listTarget.innerHTML = ""
    this.resetPaging()
  }

  highlight(options) {
    options.forEach((opt, i) => opt.classList.toggle("bg-gray-100", i === this.selectedIndex))
    if (this.selectedIndex >= 0) options[this.selectedIndex]?.scrollIntoView({ block: "nearest" })
  }

  selectableOptions() {
    return Array.from(this.listTarget.children).filter(el => el.dataset.userId)
  }

  hide(event) {
    if (!this.element.contains(event.target)) this.closeList()
  }

  resetPaging() {
    this.page = 1
    this.query = this.inputTarget?.value?.trim() || ""
    this.hasMore = true
    this.loading = false
    this.selectedIndex = -1
  }

  escape(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
